// @ts-strict-ignore
import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'src/store/store';
import i18n from 'src/utils/translate';
import { useUnsavedChangesPrompt } from 'src/utils/hooks/useUnsavedChangesPrompt';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Section } from 'src/constants/footfall';
import { saveAboutData } from 'src/modules/community/communityAbout/actions';
import { setAlert } from 'src/components/alert/actions';
import ContentEditor from 'src/components/textEditorSimple/components/contentEditor';
import { composeHtmlStringFromBB } from 'src/components/textEditor/deserialiseContent';
import { validateAbout } from 'src/modules/community/communityAbout/components/validateAbout';
import Button from 'src/components/buttons/button';

export default function AboutEditorAndroid() {
  const dispatch = useDispatch();
  const { description: defaultDescription, isLoading } = useSelector(({ pages }) => pages.communityAbout);
  const [description, setDescription] = useState(defaultDescription || '');
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [isReset, setIsReset] = useState(false);

  const submit = () => {
    // Display errors, if any, after user submitted
    const revalidateErrors = validateAbout({ description, setIsDirty, setIsSubmitDisabled });
    if (revalidateErrors.length) {
      return dispatch(setAlert(revalidateErrors[revalidateErrors.length - 1]));
    }
    dispatch(saveAboutData({ description }));
    trackFootfallEvent(Event.CLICKED, {
      clickType: 'save-description',
      clickMetadata: { section: Section.CommunityAbout },
    });
  };

  // Deleting description scenario: trim before validating because an empty android editor value always contains one line break
  const debouncedValidate = useCallback(debounce(() => validateAbout({ description, setIsDirty, setIsSubmitDisabled }), 200), [description]);

  useEffect(() => {
    debouncedValidate();
  }, [debouncedValidate]);

  useUnsavedChangesPrompt({ unsavedChanges: isDirty, onAccept: () => setIsReset(true) });

  useEffect(() => { // Disable Save button when we are saving
    setIsSubmitDisabled(isLoading);
  }, [isLoading]);

  return (
    <EditorContainer>
      <ContentEditor
        autoFocus={true}
        dataTestId="community-description-editor"
        hasImage={false}
        hasMentions={false}
        initialHtml={composeHtmlStringFromBB({ text: description || '' })}
        isReset={isReset}
        placeholder={i18n.t('Describe who is this community for and with what objectives...')}
        submitButton={(
          <SaveButton
            dataTestId="save-description-button"
            disabled={isSubmitDisabled}
            text={i18n.t('Save')}
            onClick={submit}
          />
        )}
        onChanged={(data: { body: string }) => setDescription(data.body.trim())}
      />
    </EditorContainer>
  );
}

const EditorContainer = styled.div`
  width: 100%;
  height: auto;
  padding: 10px 10px 0;
  border: ${({ theme }) => theme.greyLine};
`;

const SaveButton = styled(Button)`
  float: right;
  margin: 0;
`;
